import { useSelector } from "react-redux";

import { BigNumberSkeleton } from "@/components/ui/chart/ChartLoadingWrapper";
import { MESSAGE } from "@/constants/message";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";

type GeneralSummaryProps = {
  quoteTxt?: string | null;
  isFetching?: boolean;
};

export default function GeneralSummary({
  quoteTxt,
  isFetching
}: Readonly<GeneralSummaryProps>) {
  const { isDatasetWidgetEnabled } = useAppFeatures();
  const { generalSummary, isHasSummary } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  return (
    <div className={"flex flex-col bg-white print:grow mb-4"}>
      <div>
        <h2 className="text-base font-semibold text-txtHeading">
          General Summary
        </h2>
        {quoteTxt && <p className="text-xs my-2">{quoteTxt}</p>}
      </div>
      {isFetching && isDatasetWidgetEnabled && (
        <div
          data-testid="general-summary-skeleton"
          className="mt-3 grid gap-2 grid-cols-2 grow print:mt-4"
        >
          <BigNumberSkeleton />
          <BigNumberSkeleton />
          <BigNumberSkeleton />
          <BigNumberSkeleton />
        </div>
      )}

      {isHasSummary && !isFetching ? (
        <div className={"mt-3 grid gap-2 grid-cols-2 grow print:mt-4"}>
          {/* Max Score */}
          <div className="bg-white flex flex-row rounded-xl py-2 px-3 gap-3 border border-gainsboro overflow-auto">
            <div className="w-1 h-full rounded-full bg-solid-green shrink-0" />
            <div className="flex flex-col">
              <p
                id="maxScoreLabel"
                className="text-txtBody text-xs font-semibold"
              >
                Max. Score
              </p>
              <p
                aria-labelledby="maxScoreLabel"
                className="text-primary-teal text-2xl font-semibold"
              >
                {(generalSummary?.data.general.max_score ?? 0).toLocaleString(
                  "en-US",
                  { maximumFractionDigits: 3 }
                )}
              </p>
            </div>
          </div>

          {/* Avg Score */}
          <div className="bg-white flex flex-row rounded-xl py-2 px-3 gap-3 border border-gainsboro text-ellipsis overflow-auto">
            <div className="w-1 h-full rounded-full bg-tangerine shrink-0" />
            <div className="flex flex-col">
              <p
                id="avgScoreLabel"
                className="text-txtBody text-xs font-semibold"
              >
                Avg. Score
              </p>
              <p
                aria-labelledby="avgScoreLabel"
                className="text-primary-teal text-2xl font-semibold text-ellipsis overflow-auto"
              >
                {(generalSummary?.data.general.avg_score ?? 0).toLocaleString(
                  "en-US",
                  { maximumFractionDigits: 3 }
                )}
              </p>
            </div>
          </div>

          {/* Min Score */}
          <div className="bg-white flex flex-row rounded-xl py-2 px-3 gap-3 border border-gainsboro text-ellipsis overflow-auto">
            <div className="w-1 h-full rounded-full bg-solid-red shrink-0" />
            <div className="flex flex-col">
              <p
                id="minScoreLabel"
                className="text-txtBody text-xs font-semibold"
              >
                Min. Score
              </p>
              <p
                aria-labelledby="minScoreLabel"
                className="text-primary-teal text-2xl font-semibold"
              >
                {(generalSummary?.data.general.min_score ?? 0).toLocaleString(
                  "en-US",
                  { maximumFractionDigits: 3 }
                )}
              </p>
            </div>
          </div>

          {/* Median Score */}
          <div className="bg-white flex flex-row rounded-xl py-2 px-3 gap-3 border border-gainsboro text-ellipsis overflow-auto">
            <div className="w-1 h-full rounded-full bg-solid-blue shrink-0" />
            <div className="flex flex-col">
              <p
                id="medianScoreLabel"
                className="text-txtBody text-xs font-semibold"
              >
                Median. Score
              </p>
              <p
                aria-labelledby="medianScoreLabel"
                className="text-primary-teal text-2xl font-semibold "
              >
                {(
                  generalSummary?.data.general.median_score ?? 0
                ).toLocaleString("en-US", { maximumFractionDigits: 3 })}
              </p>
            </div>
          </div>
        </div>
      ) : (
        !isFetching && (
          <div className="mt-3 bg-white-smoke flex-col w-full items-center justify-center text-center p-5 rounded-xl print:mt-4">
            <p className="text-txtBody text-sm font-semibold">
              {MESSAGE.REPORT_SUMMARY_NO_SCORES_AVAILABLE_TITLE}
            </p>
            <p className="text-txtBody text-xs">
              {MESSAGE.REPORT_SUMMARY_NO_SCORES_AVAILABLE_DESCRIPTION}
            </p>
          </div>
        )
      )}
    </div>
  );
}
